import { useMemo } from "react";
import { StatusBadge } from "../../../../../constants/statusConstants";
import CustomSelect from "../../../components/CustomSelect";
import CustomTable from "../../../components/CustomTable";
import { EmptyChart, hasChartData } from "../../../components/EmptyChart";
import Loading from "../../../components/Loading";
import PieChart from "../../../components/PieChart";
import SearchAndFilterBar from "../../../components/SearchAndFilterBar";

const IntellectualPropertyReportView = ({
  chartData,
  currentPage,
  customerOptions,
  customerPage,
  customerTotalPages,
  exporting,
  fromDate,
  initialLoading,
  isEmbedded,
  loadingCustomers,
  search,
  selectedClient,
  serviceTypeCount,
  tableData,
  tableLoading,
  toDate,
  totalPages,
  totalProfiles,
  visibleProfiles,
  handleApplyFilter,
  handleCustomerDropdownOpen,
  handleExportPdf,
  handleLoadMoreCustomers,
  handleReportPageChange,
  handleSearchCustomer,
  onRowClick,
  setFromDate,
  setSearch,
  setSelectedClient,
  setToDate,
}) => {
  const columns = useMemo(
    () => [
      {
        key: "id",
        title: "STT",
        minWidth: "60px",
        align: "text-left",
        render: (value) => <div className="pl-2">{value}</div>,
      },
      {
        key: "mau",
        title: "Mẫu",
        minWidth: "96px",
        align: "text-center",
        render: (value) => (
          <div className="flex items-center justify-center">
            <img
              src={value}
              alt="Hình mẫu"
              className="size-14 rounded-xl object-contain bg-white"
            />
          </div>
        ),
      },
      { key: "ten", title: "Tên gọi", minWidth: "180px" },
      { key: "phanLoai", title: "Phân loại", minWidth: "120px" },
      {
        key: "soDon",
        title: "Số đơn",
        minWidth: "120px",
        color: "text-[#0954E9]",
      },
      { key: "ngayNopDon", title: "Ngày nộp đơn", minWidth: "140px" },
      { key: "soBang", title: "Số bằng", minWidth: "120px" },
      { key: "chuSoHuu", title: "Chủ sở hữu", minWidth: "180px" },
      {
        key: "trangThai",
        title: "Tình trạng",
        minWidth: "140px",
        align: "text-right",
        render: (value) => <StatusBadge status={value} />,
      },
    ],
    [],
  );

  if (initialLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loading />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full min-h-0">
      {!isEmbedded && (
        <div className="shrink-0 flex justify-between items-center mb-6">
          <h1 className="text-2xl font-sf-semibold text-gray-800">
            Danh mục tài sản trí tuệ
          </h1>
        </div>
      )}

      <div className="w-fullflex-1 min-h-0 overflow-visible">
        <div
          className={`${!isEmbedded ? "w-full bg-white rounded-[16px] border border-[#D2D5DA] shadow-sm p-4" : ""} flex flex-col gap-4 h-full min-h-0 overflow-visible`}
        >
          <div className="w-full shrink-0 flex flex-col gap-4 2xl:flex-row 2xl:items-center 2xl:justify-between">
            <div className="w-full flex flex-wrap items-center gap-3 2xl:justify-end">
              {!isEmbedded && (
                <div className="w-[290px]">
                  <CustomSelect
                    options={customerOptions}
                    placeholder="Chọn khách hàng"
                    value={selectedClient}
                    onChange={setSelectedClient}
                    searchable
                    height="42px"
                    infinite
                    loadMore={handleLoadMoreCustomers}
                    hasMore={customerPage < customerTotalPages}
                    loadingMore={loadingCustomers}
                    onSearch={handleSearchCustomer}
                    onDropdownOpen={handleCustomerDropdownOpen}
                    controlClassName="rounded-lg"
                    dropdownClassName="rounded-lg"
                  />
                </div>
              )}

              <SearchAndFilterBar
                searchPlaceholder="Tìm kiếm tài sản trí tuệ"
                searchValue={search}
                onSearchChange={(event) => setSearch(event.target.value)}
                onSearchClick={handleApplyFilter}
                showDatePickers
                fromDatePlaceholder="Từ ngày"
                fromDateValue={fromDate}
                toDatePlaceholder="Đến ngày"
                toDateValue={toDate}
                onFromDateChange={setFromDate}
                onToDateChange={setToDate}
                showFilter
                filterLabel="Áp dụng"
                showAdditionBtn={false}
              />

              <div className="ml-auto">
                <button
                  type="button"
                  onClick={handleExportPdf}
                  disabled={exporting || tableLoading}
                  className="inline-flex h-[42px] items-center justify-center gap-2 rounded-lg bg-[#0457C2] px-4 text-sm font-sf-medium text-white transition-colors hover:bg-[#0348a3] active:scale-[0.99] disabled:cursor-wait disabled:opacity-70"
                >
                  <img
                    src="/icons/download.svg"
                    alt=""
                    className="h-4 w-4 brightness-0 invert"
                  />
                  <span>{exporting ? "Đang xuất..." : "Xuất file PDF"}</span>
                </button>
              </div>
            </div>
          </div>

          <div className="flex-1 min-h-0 grid grid-cols-1 gap-4 xl:grid-cols-3">
            <div className="rounded-[12px] border border-[#E3E3E3] p-4 w-full min-h-[360px] overflow-visible">
              <div className="flex items-center justify-center h-full">
                {hasChartData(chartData) ? (
                  <PieChart
                    data={chartData}
                    size={280}
                    legendPosition="bottom"
                  />
                ) : (
                  <EmptyChart title="Chưa có dữ liệu tài sản trí tuệ" />
                )}
              </div>
            </div>

            <div className="xl:col-span-2 flex flex-col min-h-0 overflow-visible">
              <div className="flex-1 min-h-0 overflow-visible">
                <div className="h-full min-h-0 overflow-auto p-[2px]">
                  <CustomTable
                    columns={columns}
                    data={tableData}
                    setView={onRowClick}
                    setEdit={false}
                    setDelete={false}
                    actionColumnTitle="Thao tác"
                    actionColumnWidth="104px"
                    stickyActionColumn
                    currentPage={currentPage}
                    setCurrentPage={handleReportPageChange}
                    totalPages={totalPages}
                    loading={tableLoading}
                    loadingText="Đang tải danh mục tài sản trí tuệ..."
                    onRowClick={onRowClick}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IntellectualPropertyReportView;
